  /* ================== Utility ================== */
  const getCartLoader = () => document.querySelector('.drawer__inner cart-loader');

  function showLoader() {
    getCartLoader()?.show();
  }

  function hideLoader() {
    getCartLoader()?.hide();
  }

  /* ================== Cart Loader Component ================== */
  class CartLoader extends HTMLElement {
    show() {
      this.classList.add('show-loader');
    }

    hide() {
      this.classList.remove('show-loader');
    }

    toggle(state) {
      this.classList.toggle('show-loader', state);
    }
  }

  customElements.define('cart-loader', CartLoader);

  document.body.addEventListener('click', handleCartInteraction);
  document.body.addEventListener('change', handleCartInteraction);

  function handleCartInteraction(e) {
    const drawer = e.target.closest('.drawer__inner');
    if (!drawer) return;

    const loader = drawer.querySelector('cart-loader');
    if (!loader || loader.classList.contains('show-loader')) return;

    const isQuantityButton = e.target.closest('.quantity__button');
    const isRemoveButton = e.target.closest('.cart-remove-button');
    const isQuantityInput = e.type === 'change' && e.target.matches('.quantity__input');

    if (isQuantityButton || isRemoveButton || isQuantityInput) {
      setTimeout(() => loader.show(), 50);
      setTimeout(() => loader.hide(), 1000);
    }
  }

  /* ================== Upsell Products ================== */
  class UpsellProducts extends HTMLElement {
    constructor() {
      super();
      this.cartDrawer = document.querySelector('cart-drawer');
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
    }

    async onClick(event) {
      const button = event.target.closest('.upsell-add-button');
      if (!button || button.disabled) return;

      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const card = button.closest('.upsell-product');
      const originalText = button.textContent;

      button.disabled = true;
      button.textContent = 'Adding...';
      showLoader();
      this.cartDrawer?.open();

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });

        const [drawerHtml, bubbleHtml] = await Promise.all([
          fetch('/?section_id=cart-drawer').then(r => r.text()),
          fetch('/?section_id=cart-icon-bubble').then(r => r.text())
        ]);

        this.cartDrawer?.renderContents({
          sections: {
            'cart-drawer': drawerHtml,
            'cart-icon-bubble': bubbleHtml
          }
        });

        card.style.opacity = '0';
        setTimeout(() => {
          card.remove();
          if (!this.querySelector('.upsell-product')) this.remove();
        }, 300);
      } catch (err) {
        console.error('Upsell add failed', err);
      } finally {
        hideLoader();
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  }

  customElements.define('upsell-products', UpsellProducts);

  /* ================== Protection Toggle ================== */

class ProtectionToggle extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('[data-protection-toggle]');
    this.variantId = this.toggle?.dataset.variantId;
    this.isUpdating = false;

    if (this.toggle) {
      this.toggle.addEventListener('click', () => {
        if (!this.isUpdating) {
          this.updateProtection(this.toggle.checked);
        }
      });
    }

    document.addEventListener('cart:updated', () => {
      this.checkCartAndRemoveIfNeeded();
    });

    this.checkCartAndRemoveIfNeeded();
  }

  async updateProtection(isWanted) {
    this.isUpdating = true;
    try {
      const cart = await this.fetchCart();
      const protectionItem = this.findProtectionItem(cart);
      const hasOtherItems = cart.items.some(item => item.variant_id.toString() !== this.variantId);

      if (!hasOtherItems && protectionItem) {
        await this.removeProtectionItem(protectionItem.key);
        return;
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            shipping_protection: isWanted ? 'Yes' : 'No'
          },
          updates: {
            [this.variantId]: isWanted ? 1 : 0
          }
        })
      });

      const updatedCart = await this.fetchCart();
      const stillHasProtection = this.findProtectionItem(updatedCart) !== undefined;
      this.toggle.checked = stillHasProtection;

      await this.refreshCartDrawer();
    } catch (error) {
      console.error('Error updating protection:', error);
      this.toggle.checked = !isWanted;
    } finally {
      this.isUpdating = false;
    }
  }

  async checkCartAndRemoveIfNeeded() {
    if (this.isUpdating) return;

    try {
      const cart = await this.fetchCart();
      const protectionItem = this.findProtectionItem(cart);
      const hasOtherItems = cart.items.some(item => item.variant_id.toString() !== this.variantId);

      if (!hasOtherItems && protectionItem) {
        this.isUpdating = true;
        await this.removeProtectionItem(protectionItem.key);
      }
    } catch (err) {
      console.error('Error checking cart and removing protection:', err);
    } finally {
      this.isUpdating = false;
    }
  }

async removeProtectionItem(itemKey) {
  showLoader();

  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: itemKey, quantity: 0 })
  });

  await this.refreshCartDrawer();
}

async refreshCartDrawer() {
  try {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    showLoader(); // 👈 keep loader active

    const [drawerHtml, bubbleHtml] = await Promise.all([
      fetch(`/?section_id=cart-drawer`).then(r => r.text()),
      fetch(`/?section_id=cart-icon-bubble`).then(r => r.text())
    ]);

    const isEmpty = /drawer__inner-empty/.test(drawerHtml);

    if (cartDrawer.renderContents) {
      cartDrawer.renderContents({
        sections: {
          'cart-drawer': drawerHtml,
          'cart-icon-bubble': bubbleHtml
        }
      });

      cartDrawer.classList.toggle('is-empty', isEmpty);
    }

    // 👇 Only hide loader *after* render, and only if empty state is present
    requestAnimationFrame(() => {
      if (isEmpty) {
        // Give the empty state a frame to settle, then hide loader
        requestAnimationFrame(() => hideLoader());
      } else {
        hideLoader(); // Normal flow
      }
    });

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        sections: {
          'cart-drawer': drawerHtml,
          'cart-icon-bubble': bubbleHtml
        }
      }
    }));
  } catch (error) {
    console.error('Error refreshing cart drawer and bubble:', error);
    hideLoader();
  }
}


  async fetchCart() {
    const res = await fetch('/cart.js');
    return await res.json();
  }

  findProtectionItem(cart) {
    return cart.items.find(item => item.variant_id.toString() === this.variantId);
  }
}

customElements.define('protection-toggle', ProtectionToggle);


  /* ================== Bundle Swap ================== */
  class BundleSwap extends HTMLElement {
    constructor() {
      super();
      this.cartDrawer = document.querySelector('cart-drawer');
      this.LIFETIME_GUARANTEE_ID = '49797773590794';
      this.button = this.querySelector('.bundle-swap-button');
    }

    connectedCallback() {
      if (this.button) {
        this.button.addEventListener('click', this.swap.bind(this));
      }
    }

    async swap() {
      const currentVariantId = this.dataset.currentVariant;
      const bundleVariantId = parseInt(this.dataset.bundleVariant);
      const quantity = parseInt(this.dataset.quantity);

      if (!bundleVariantId) return;

      this.toggleButtonLoading(true);
      showLoader();
      this.cartDrawer?.open();

      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: {
              [currentVariantId]: 0,
              [this.LIFETIME_GUARANTEE_ID]: 0
            }
          })
        });

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: bundleVariantId, quantity }]
          })
        });

        const [drawerHtml, bubbleHtml] = await Promise.all([
          fetch('/?section_id=cart-drawer').then(r => r.text()),
          fetch('/?section_id=cart-icon-bubble').then(r => r.text())
        ]);

        this.cartDrawer?.renderContents({
          sections: {
            'cart-drawer': drawerHtml,
            'cart-icon-bubble': bubbleHtml
          }
        });

      } catch (error) {
        this.showError('Failed to update cart');
        console.error('Bundle upgrade failed:', error);
      } finally {
        hideLoader();
        this.toggleButtonLoading(false);
      }
    }

    toggleButtonLoading(loading) {
      if (!this.button) return;
      this.button.disabled = loading;
      this.button.innerHTML = loading
        ? `<span class="loading-spinner" aria-hidden="true"></span><span>UPGRADING...</span>`
        : 'UPGRADE & SAVE';
    }

    showError(message) {
      const errorSpan = this.querySelector('.upsell-message');
      if (!errorSpan) return;

      errorSpan.textContent = message;
      errorSpan.hidden = false;

      setTimeout(() => {
        errorSpan.hidden = true;
      }, 3000);
    }
  }

  customElements.define('bundle-swap', BundleSwap);
